import { createStyles, FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExactTagsWithExclusionTagsFilter } from '../../../generated/model/exactTagsWithExclusionTagsFilter';
import { Classifier } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import InputAutocomplete, { AutocompleteOption } from '../../Inputs/Autocomplete';
import {
  formatClassifiersToOptions,
  mapSelectedAutocompleteOptions,
} from '../../Inputs/Autocomplete/autocomplete.utils';
import ReadableStatementView from '../../Inputs/ReadableStatementView';

interface Props {
  root?: boolean;
  active: boolean;
  items?: Classifier[];
  excludeItems?: Classifier[];
  defaultValue?: ExactTagsWithExclusionTagsFilter;
  onChange: (value: ExactTagsWithExclusionTagsFilter) => void;
}

const CountryTagsExclusionFilterInput: React.FC<Props> = React.memo(
  ({
    root,
    active,
    items = [],
    excludeItems = [],
    defaultValue = {
      value: {
        tags: { excludeValues: [], includeValues: [] },
      },
    },
    onChange,
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState<ExactTagsWithExclusionTagsFilter>(defaultValue);
    const [toggleOpenState, setTogglerOpenState] = useState<boolean>(false);

    const includeOptions: AutocompleteOption[] = useMemo(() => formatClassifiersToOptions(items), [
      items,
    ]);
    const excludeOptions: AutocompleteOption[] = useMemo(
      () => formatClassifiersToOptions(excludeItems),
      [excludeItems],
    );

    function getValuesNames(value: string[], options: AutocompleteOption[]) {
      const valuesList = value.map((v) => options.find((o) => o.value === v)?.label);
      return valuesList.join(', ');
    }

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const rs: any[] = [];
    if (
      (state.value?.tags?.includeValues && state.value.tags.includeValues.length) ||
      (state.value?.tags && !Array.isArray(state.value.tags.includeValues))
    ) {
      rs.push({
        label: 'Included:',
        value: getValuesNames(state.value.tags.includeValues ?? [], includeOptions),
      });
    }
    if (
      (state.value?.tags?.excludeValues && state.value.tags.excludeValues.length) ||
      (state.value?.tags && !Array.isArray(state.value.tags.excludeValues))
    ) {
      rs.push({
        label: 'NOT included:',
        value: getValuesNames(state.value.tags.excludeValues ?? [], excludeOptions),
      });
    }

    useEffect(() => {
      updateToggle(toggleOpenState);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const updateToggle = (value: boolean) => {
      if (state.value && state.value.tags && state.value.tags.excludeValues?.length) {
        setTogglerOpenState(true);
      } else {
        setTogglerOpenState(value);
      }
    };

    return (
      <>
        {active ? (
          <>
            <FormControl className={`form-control ${classes.root}`}>
              <InputAutocomplete
                defaultValue={mapSelectedAutocompleteOptions(
                  includeOptions,
                  state.value?.tags!!.includeValues,
                )}
                onChange={(includeValue) => {
                  setState({
                    ...state,
                    value: {
                      tags: {
                        includeValues: includeValue.map(
                          (option: AutocompleteOption) => option.value,
                        ),
                        excludeValues:
                          state.value && state.value.tags ? state.value.tags.excludeValues : [],
                      },
                    },
                  });
                }}
                options={includeOptions}
              />
              <div
                className={classes.tagToggler}
                onClick={() => setTogglerOpenState(!toggleOpenState)}
              >
                <FormHelperText>{t('app:notIncluded')}</FormHelperText>
                {toggleOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
              </div>

              {toggleOpenState ? (
                <InputAutocomplete
                  defaultValue={mapSelectedAutocompleteOptions(
                    excludeOptions,
                    state.value?.tags!!.excludeValues,
                  )}
                  onChange={(excludeValue) => {
                    setState({
                      ...state,
                      value: {
                        tags: {
                          excludeValues: excludeValue.map(
                            (option: AutocompleteOption) => option.value,
                          ),
                          includeValues:
                            state.value && state.value.tags ? state.value.tags.includeValues : [],
                        },
                      },
                    });
                  }}
                  options={excludeOptions}
                />
              ) : null}
            </FormControl>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      '.Mui-expanded': {
        margin: 0,
      },
    },
    tagToggler: {
      display: 'flex',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: theme.typography.fontSize - 2,
    },
  }),
);

export default CountryTagsExclusionFilterInput;
